import { useState } from 'react';

import { useEffect, useRef } from 'react';
import { Popover, Badge } from 'antd';
import { BiBell } from 'react-icons/bi';
import { notification } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { addNotification, markAllAsRead } from '../../../store/slices/notificationSlice';
import SocketAPI from '../../socket';

export default function NotificationPopover() {
  const dispatch = useDispatch();
  const { items: notifications, unreadCount } = useSelector((state) => state.notifications);
  const [open, setOpen] = useState(false);
  const audioRef = useRef(null);
  const socket = SocketAPI();

  useEffect(() => {
    // Listen for message:alert events
    socket?.on('message:alert', (payload) => {
      // Create the new notification
      const newNotification = {
        id: Date.now(),
        title: `${payload?.sender?.first_name} ${payload?.sender?.last_name ?? ''}`,
        description: payload.message || '',
        time: 'Just now',
        read: false,
        ...payload,
      };

      // Dispatch to Redux store
      dispatch(addNotification(newNotification));

      // Play sound alert if audio element exists
      if (audioRef.current) {
        audioRef.current.play().catch(() => {});
      }

      // Show a notification
      notification.info({
        message: newNotification.title,
        description: newNotification.description,
      });
    });

    // Cleanup function
    return () => {
      socket?.off('message:alert');
    };
  }, [socket, dispatch]);

  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);

    // Mark all as read when opening the popover
    if (newOpen && unreadCount > 0) {
      dispatch(markAllAsRead());
    }
  };

  const content = (
    <div className="notification-popover" style={{ width: 320 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 12,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <h3 style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>Notifications</h3>
      </div>

      {/* Scrollable container for all notifications */}
      <div style={{ maxHeight: 320, overflowY: 'auto', padding: 8 }}>
        {notifications.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {notifications.map((notification) => (
              <div
                key={notification.id}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  borderLeft: '4px solid',
                  borderLeftColor: notification.read ? '#d9d9d9' : '#1890ff',
                  backgroundColor: notification.read ? '#f5f5f5' : '#e6f7ff',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <h4 style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>{notification.title}</h4>
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>{notification.time}</span>
                </div>
                <p
                  style={{
                    fontSize: 12,
                    color: '#8c8c8c',
                    marginTop: 4,
                    marginBottom: 0,
                  }}
                >
                  {notification.description}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <p style={{ color: '#8c8c8c' }}>No notifications</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Audio element for notification sound */}
      <audio ref={audioRef} src="/admin/notification.wav" preload="auto" />

      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={handleOpenChange}
        placement="bottomRight"
        arrow={false}
      >
        <div style={{ position: 'relative', cursor: 'pointer' }}>
          <Badge count={unreadCount} overflowCount={9} size="small">
            <BiBell style={{ fontSize: 20 }} />
          </Badge>
        </div>
      </Popover>
    </>
  );
}
